import { getCanonicalOrigin, canonicalUrl as buildCanonicalUrlFromOrigin } from "./appOrigin";

export const CANONICAL_ORIGIN = getCanonicalOrigin();

export const DEFAULT_OG_IMAGE =
  "https://lessonlaunchpad.com/social-share-image.jpg";

export const DEFAULT_SITE_NAME = "Lesson Launchpad™";

export const DEFAULT_PAGE_SEO = {
  title: "Lesson Launchpad™ - Automate Classroom Routines",
  description:
    "Automate timers, agendas, reminders, transitions, and music so teachers run class from bell to bell with less manual work.",
  path: "/",
  image: DEFAULT_OG_IMAGE,
  ogType: "website",
  siteName: DEFAULT_SITE_NAME,
};

function upsertMetaByName(name, content) {
  if (!content) {
    return;
  }

  let element = document.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertMetaByProperty(property, content) {
  if (!content) {
    return;
  }

  let element = document.querySelector(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function removeMetaByName(name) {
  document.querySelector(`meta[name="${name}"]`)?.remove();
}

function upsertLinkRel(rel, href) {
  if (!href) {
    document.querySelector(`link[rel="${rel}"]`)?.remove();
    return;
  }

  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function isStageHostname(hostname) {
  return hostname === "stage.lessonlaunchpad.com"
    || hostname === "www.stage.lessonlaunchpad.com";
}

export function getRobotsContentForHostname(hostname, { noIndex = false, robots } = {}) {
  if (isStageHostname(hostname)) {
    return "noindex,nofollow";
  }

  if (robots) {
    return robots;
  }

  if (noIndex) {
    return "noindex,follow";
  }

  return null;
}

function getRobotsContent(seo = {}) {
  const hostname = typeof window !== "undefined" ? window.location?.hostname : undefined;
  return getRobotsContentForHostname(hostname, seo);
}

export function buildCanonicalUrl(path = "/") {
  return buildCanonicalUrlFromOrigin(path);
}

export function applyPageSeo(seo = DEFAULT_PAGE_SEO) {
  const {
    title,
    description,
    path = "/",
    image = DEFAULT_OG_IMAGE,
    ogType = "website",
    siteName = DEFAULT_SITE_NAME,
    ogTitle,
    ogDescription,
    noIndex = false,
    robots,
  } = seo;
  const canonicalUrl = buildCanonicalUrl(path);
  const robotsContent = getRobotsContent({ noIndex, robots });
  const socialTitle = ogTitle || title;
  const socialDescription = ogDescription || description;

  document.title = title;
  upsertMetaByName("description", description);
  upsertMetaByProperty("og:title", socialTitle);
  upsertMetaByProperty("og:description", socialDescription);
  upsertMetaByProperty("og:site_name", siteName);
  upsertMetaByProperty("og:url", canonicalUrl);
  upsertMetaByProperty("og:type", ogType);
  upsertMetaByProperty("og:image", image);
  upsertMetaByName("twitter:card", "summary_large_image");
  upsertMetaByName("twitter:title", socialTitle);
  upsertMetaByName("twitter:description", socialDescription);
  upsertMetaByName("twitter:image", image);
  upsertLinkRel("canonical", canonicalUrl);

  if (robotsContent) {
    upsertMetaByName("robots", robotsContent);
  } else {
    removeMetaByName("robots");
  }
}

export function restoreDefaultSeo() {
  applyPageSeo(DEFAULT_PAGE_SEO);
}
